/**
 * Permission-aware affordance helpers shared by the host app and plugins.
 *
 * The host had a `CanGuard` under `web/src/components/auth/`, which plugin
 * packages cannot import (`scripts/check-plugin-boundaries.sh` rule 1), so
 * every plugin surface either hand-rolled a `can(...)` check or shipped an
 * ungated button that 403s on click. This module is the shared home: it
 * reads the same `PluginAuthState.can` the host builds from
 * `/api/auth/me` → `memberships[].permissions`.
 *
 * **Pick the right check.** Owner-scoped resources (personas, gateways, MCP
 * configs, flows, projects) are *not* gated on a bare permission slug —
 * their routes run `assertCanWriteOwned`, where the slug is only required
 * to write the org-wide tier. Gating those on {@link usePermission} makes
 * the client stricter than the server and hides a control the user could
 * legitimately have used. Use {@link useCanWriteOwned} for anything with an
 * `ownerId`, and {@link usePermission} only for genuinely org-wide actions.
 *
 * Note what is *not* here: a "disabled create button" helper. Creating an
 * owner-scoped resource is open to every member, because the server defaults
 * their ownership to themselves (`OwnerField` submits an explicit owner,
 * defaulting the picker to the caller's own user). The narrowest
 * correct gate on a create form is the **Organization** owner option, which
 * `OwnerField`'s `organizationPermission` prop handles.
 *
 * @module @reflex/ui/client/permissions
 */
import type { ReactNode } from 'react';
import { usePluginAuth } from './PluginClientContext';
import type { PluginAuthState } from './interfaces';

/** Options shared by {@link usePermission} and {@link CanGuard}. */
export interface PermissionCheckOptions {
  /** Require every slug rather than any one of them. Defaults to any. */
  all?: boolean;
  /**
   * Check within this team as well as org-wide — the client mirror of the
   * server's `canInTeam`, which every route with a team id in its path
   * uses. Pass it whenever the control acts on one named team, or a user
   * whose only authority over that team comes from their team role sees it
   * hidden while the server would have let them through.
   */
  team?: string | null;
}

/**
 * True when the active membership holds the permission(s).
 *
 * Two bypasses, both mirroring the server: a platform admin passes
 * (`isPlatformAdmin`), and an install with auth disabled has no permission
 * model to consult so everything is allowed. Everything else is a plain set
 * membership test against the active org's permission list — the org-wide
 * slugs, matching the server's `can()`.
 *
 * Pass `options.team` for a control that acts on one named team: the
 * server evaluates those routes with `canInTeam`, so the caller's role on
 * that team counts too, and a check that ignored it would hide a control
 * the user could legitimately have used.
 *
 * Access to a resource owned by another user is never a slug — it is a
 * per-resource grant the client cannot enumerate — so do not try to model
 * it here. For anything with an `ownerId`, use {@link useCanWriteOwned}
 * instead: owner-scoped routes only require the slug for the org-wide tier.
 */
export function usePermission(
  permission: string | readonly string[],
  options: PermissionCheckOptions = {},
): boolean {
  const auth = usePluginAuth();
  if (auth.authEnabled === false) return true;
  const slugs = typeof permission === 'string' ? [permission] : permission;
  if (slugs.length === 0) return true;
  if (auth.isPlatformAdmin) return true;
  const teamSlugs = options.team ? teamPermissions(auth, options.team) : null;
  const holds = (slug: string) => auth.can(slug) || (teamSlugs?.has(slug) ?? false);
  return options.all ? slugs.every(holds) : slugs.some(holds);
}

const EMPTY_PERMISSION_SET: ReadonlySet<string> = new Set();

/** The slugs the caller's role on `teamId` carries; empty when not a member. */
function teamPermissions(auth: PluginAuthState, teamId: string): ReadonlySet<string> {
  const team = auth.activeMembership?.teams?.find((t) => t.id === teamId);
  return team ? new Set(team.permissions) : EMPTY_PERMISSION_SET;
}

/**
 * The slice of {@link PluginAuthState} {@link canWriteOwned} reads.
 *
 * Deliberately excludes `isPlatformAdmin`: owner-scoped routes grant an
 * admin nothing a member lacks, so the flag has no place in this decision
 * and leaving it out makes reintroducing a bypass a visible type change.
 */
export interface OwnedWriteAuth {
  authEnabled?: boolean;
  activeOrganizationId: string | null;
  can: (permission: string) => boolean;
}

/**
 * Whether the caller may write an owner-scoped resource — the client
 * mirror of `assertCanWriteOwned` in `@reflex/plugin-api/owner-scope`.
 *
 * The server's rule, in full:
 *
 *   1. `ownerId === null` (host tier) — denied outright. The only escape
 *      is `caller.bypassOwnerForElevated`, and the host's
 *      `buildOwnerScope` never sets it: a platform admin is owner-scoped
 *      exactly like a member (see `server/src/lib/owner-scope.ts`). So
 *      this is denied for *every* caller a request can produce, admins
 *      included, and `isPlatformAdmin` must not short-circuit past it.
 *   2. `ownerId === activeOrganizationId` (org tier) — requires the strict
 *      `:write` slug, resolved from the caller's real membership roles.
 *      `isPlatformAdmin` must not short-circuit past this one either: the
 *      plugin-facing `authz.can` that `hasWritePermission` calls passes
 *      `isSuperAdmin: false` deliberately, so "the admin flag can never
 *      widen access through this API" (`createPluginAuthzApi` in
 *      `server/src/lib/authz.ts`). A platform admin who is a plain member
 *      of the active org is denied the org tier exactly like any member.
 *   3. Any other owner — allowed iff the caller belongs to that owner
 *      (`callerOwnerIds`: their user id, their teams, and every
 *      plugin-contributed owner such as a project).
 *
 * Rule 3 is why this takes no team or project list. `rowMatchesOwner` — the
 * same `callerOwnerIds` set — is what makes a row *visible* in the first
 * place, so any row the client is holding has already proved the caller
 * belongs to its owner. That equivalence is what lets this stay correct for
 * owner kinds the client cannot enumerate (projects today, whatever a
 * plugin contributes tomorrow) instead of falling back to the slug and
 * hiding controls from the project maintainers the contributor path exists
 * to serve.
 *
 * The corollary is the precondition: pass an `ownerId` that came from a
 * resource the API returned to *this* caller. Do not pass an arbitrary or
 * user-supplied owner id — rule 3 would wave it through.
 */
export function canWriteOwned(
  ownerId: string | null | undefined,
  permission: string,
  auth: OwnedWriteAuth,
): boolean {
  if (auth.authEnabled === false) return true;
  // No `isPlatformAdmin` bypass anywhere in here, unlike `usePermission`.
  // Owner-scoped routes decide through `createPluginAuthzApi`, which pins
  // `isSuperAdmin: false` on every check, and through `buildOwnerScope`,
  // which never sets `bypassOwnerForElevated`. An admin is owner-scoped and
  // slug-gated exactly like a member on these routes, so a bypass here
  // would render Edit and Delete on rows whose every mutation 403s.
  //
  // Host-tier rows in particular are visible to everyone and writable by no
  // one over HTTP, and `reason: 'host-tier'` carries no `requiredPermission`,
  // so the toast could not even explain why.
  if (ownerId == null) return false;
  if (ownerId === auth.activeOrganizationId) return auth.can(permission);
  // Every other owner: the caller belongs to it, because `rowMatchesOwner`
  // is what let them see the row at all. A `null` active org never reaches
  // here as an org-tier match, but such a request cannot write anyway —
  // the routes 400 on `requireOrgScope` before consulting ownership.
  return auth.activeOrganizationId != null;
}

/** {@link canWriteOwned} bound to the ambient auth state. */
export function useCanWriteOwned(ownerId: string | null | undefined, permission: string): boolean {
  return canWriteOwned(ownerId, permission, usePluginAuth());
}

/**
 * Copy for a control the caller lacks the permission for. Names the raw
 * slug: plugin slugs are contributed at runtime and are absent from the
 * static catalog, and `GET /permissions/catalog` is itself gated on
 * `roles:manage`, so the denied user cannot resolve a friendlier
 * description. The slug plus who to ask is the most the client can say.
 */
function permissionTooltip(permission: string): string {
  return `Requires the ${permission} permission. Ask an organization admin to grant it.`;
}

/**
 * Messages that carry no information beyond "denied", so the generic
 * {@link permissionTooltip} is strictly better than repeating them.
 */
const UNINFORMATIVE_ERRORS = new Set(['forbidden', 'permission denied', 'unauthorized']);

/**
 * Human message for a 403 the server rejected on a permission.
 *
 * The API returns `{ error, requiredPermission }` and the generated client
 * preserves it as `err.body.requiredPermission`; before this helper nothing
 * on the client read it, so a denied write surfaced as a bare "Forbidden".
 *
 * When the server's own message already explains the denial (the
 * owner-scoped routes say things like "Organization-wide resources require
 * an admin permission. Pick a personal or team owner instead.") it is kept
 * and the slug is appended, because that message names the *fix* and the
 * slug alone does not. Falls back to `fallback` when the error carries no
 * slug at all.
 */
export function permissionErrorMessage(error: unknown, fallback: string): string {
  const slug = requiredPermissionOf(error);
  if (!slug) return fallback;
  const detail = fallback.trim();
  if (!detail || UNINFORMATIVE_ERRORS.has(detail.toLowerCase().replace(/\.$/, ''))) {
    return permissionTooltip(slug);
  }
  return `${detail} Requires the ${slug} permission.`;
}

/**
 * {@link permissionErrorMessage} for the common mutation-`onError` shape,
 * where the caller has an `unknown` and wants "the server's message, plus
 * the slug when there is one, or a generic fallback".
 *
 * Exists because every owner-scoped surface was writing the same
 * `err instanceof Error ? err.message : 'Unknown error'` ternary inline and
 * then forgetting to wrap it — which silently drops the `requiredPermission`
 * the 403 carried and leaves the user a bare "Forbidden".
 */
export function permissionErrorDescription(error: unknown, fallback = 'Unknown error'): string {
  return permissionErrorMessage(error, error instanceof Error ? error.message : fallback);
}

/** Read `requiredPermission` off an API error body, if present. */
export function requiredPermissionOf(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const body = (error as { body?: unknown }).body;
  if (typeof body !== 'object' || body === null) return null;
  const slug = (body as { requiredPermission?: unknown }).requiredPermission;
  return typeof slug === 'string' && slug.length > 0 ? slug : null;
}

export interface CanGuardProps extends PermissionCheckOptions {
  permission: string | readonly string[];
  children: ReactNode;
  /** Rendered instead of `children` when the check fails. Defaults to nothing. */
  fallback?: ReactNode;
}

/**
 * Renders `children` only when {@link usePermission} passes.
 *
 * Use this for whole sections that are meaningless without the permission
 * (a danger zone, an inline create form). For a single primary action,
 * prefer rendering it disabled with an explanation — see
 * {@link permissionErrorMessage} for the wording the toasts use.
 */
export function CanGuard({ permission, children, fallback = null, all, team }: CanGuardProps) {
  const allowed = usePermission(permission, { all, team });
  return <>{allowed ? children : fallback}</>;
}
